import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAPI, useResearcherAPI } from "../../../hooks";
import {
  ISelectedHealthCategories,
  TeamMemberStateTypes,
} from "../components/ResultMainForm/types";
import { VALIDATION_CONST } from "../Create/const";
import { regionalCentre } from "../../../utils";
import { emailValidation } from "../Create/validations";
import { updateStandeAloneResultValidations } from "./validations";

export const useEditStandAloneResult = () => {
  const params: any = useParams();
  const navigate = useNavigate();

  const {
    getResultStandAlone,
    getHealthCategory,
    getHealthAuthorities,
    getHospitals,
  } = useAPI();

  const { getResearcherByEmail, updateStandAloneResults } = useResearcherAPI();
  const [changes, setChanges] = useState<string[]>([]);

  const [healthCategories, setHealthCategories] = useState<
    IHealthCategoryOptions[]
  >([]);
  const [healthAuthorityRegions, setHealthAuthorityRegions] = useState<
    { id: string; name: string }[]
  >([]);
  const [hospitals, setHospitals] = useState<
    { value: string; label: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [resultInfo, setResultInfo] = useState<IStandAloneResultTypes>({
    status: "pending_approval",
    is_completed: false,
    is_online: false,
    is_bc_support_unit_affiliation: false,
    is_por: false,
    is_ppo: false,
    province: "bc",
    share_on_hmc: false,
  });

  const [teamMembers, setTeamMembers] = useState<
    Record<string, TeamMemberStateTypes>
  >({});
  const [collaboratingOrganizations, setCollaboratingOrganizations] = useState<
    Record<string, string>
  >({});

  const [selectedHealthCategories, setSelectedhealthCategories] = useState<
    ISelectedHealthCategories[]
  >([]);
  const [forceChange, setForceChange] = useState(false);
  const [selectedHealthAuthorityRegions, setSelectedHealthAuthorityRegions] =
    useState<string[]>([]);

  const [selectedRegionalCenters, setSelectedRegionalCenters] = useState<
    string[]
  >([]);
  const [resourceFile, setResourceFile] = useState<File>();

  const setChangesHandler = (field: string) => {
    if (!changes.includes(field)) setChanges((prev) => [...prev, field]);
  };

  const updateResultInfoHandler = (field: string, value: any) => {
    let val = value.value || value;
    val = Number.isNaN(val) ? "" : val;

    setResultInfo((prev) => ({
      ...prev,
      [field]: val,
    }));

    setChangesHandler(field);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const addInputHandler = (type: string) => {
    const id = `${type}-${Math.round(Math.random() * 10000)}`;
    switch (type) {
      case "teamMember":
        setTeamMembers((prev) => ({
          ...prev,
          [id]: { email: "", id: "" },
        }));
        break;

      case "collaboratingOrganizations":
        setCollaboratingOrganizations((prev) => ({
          ...prev,
          [id]: "",
        }));
        break;
      default:
        break;
    }
  };

  const changeAddedInputHandler = (value: string, id: string, key: string) => {
    switch (key) {
      case "teamMember":
        setTeamMembers((prev) => ({
          ...prev,
          [id]: { email: value, id: "" },
        }));
        break;
      case "collaboratingOrganizations":
        setCollaboratingOrganizations((prev) => ({
          ...prev,
          [id]: value,
        }));
        break;
      default:
        break;
    }
    setChangesHandler(key);
  };

  const removeTeamMemberHandler = (id: string, name: string) => {
    if (Object.keys(teamMembers).length === 1) {
      const formErrors = { ...errors };
      formErrors[id] = VALIDATION_CONST.TEAM_MEMBERS.REMOVE;
      setErrors(formErrors);
      return;
    }

    const list = { ...teamMembers };
    delete list[id];
    const formErrors = errors;
    delete formErrors[name];
    setErrors({ ...formErrors });
    setTeamMembers(list);
  };

  const removeCollaboratingOrganizationsHandler = (id: string) => {
    const list = { ...collaboratingOrganizations };
    delete list[id];
    setCollaboratingOrganizations(list);
  };

  const removeInputHandler = (key: string, id: string, name?: string) => {
    switch (key) {
      case "teamMember":
        removeTeamMemberHandler(id, name || "");
        break;
      case "collaboratingOrganizations":
        removeCollaboratingOrganizationsHandler(id);
        break;
      default:
        break;
    }
    setChangesHandler(key);
  };

  const setHealthCategoriesHandler = (
    name: string,
    id: number,
    category: string
  ) => {
    const areas = selectedHealthCategories;
    const tempIndex = areas.findIndex((item) => item.id === id);
    if (tempIndex !== -1) {
      areas.splice(tempIndex, 1);
    } else {
      areas.push({ name, id, category });
    }
    setSelectedhealthCategories(areas);
    setForceChange(!forceChange);
    setChangesHandler("health category");
  };

  const addRemoveItem = (
    list: string[],
    checked: boolean,
    id: string
  ): string[] => {
    if (!checked && list.includes(id)) {
      return list.filter((item) => item !== id);
    }
    if (checked && !list.includes(id)) {
      return [...list, id];
    }

    return list;
  };

  const checkboxInputHandler = (
    field: string,
    checked: boolean,
    option: string
  ) => {
    let list;
    switch (field) {
      case "authorityRegions":
        if (option === "all") {
          list = checked
            ? healthAuthorityRegions.map((region) => region.id)
            : [];
          setSelectedHealthAuthorityRegions(list);
        } else {
          list = addRemoveItem(selectedHealthAuthorityRegions, checked, option);
          setSelectedHealthAuthorityRegions(list);
        }
        break;
      case "is_completed":
        setResultInfo({ ...resultInfo, is_completed: checked });
        break;
      case "is_online":
        setResultInfo({ ...resultInfo, is_online: checked });
        break;
      case "regional_centers":
        if (option === "all") {
          list = checked
            ? Object.keys(regionalCentre).map((region) => region)
            : [];
          setSelectedRegionalCenters(list);
        } else {
          list = addRemoveItem(selectedRegionalCenters, checked, option);
          setSelectedRegionalCenters(list);
        }
        break;
      case "is_por":
        setResultInfo({ ...resultInfo, is_por: checked });
        break;
      case "is_ppo":
        setResultInfo({ ...resultInfo, is_ppo: checked });
        break;
      case "is_bc_support_unit_affiliation":
        setResultInfo({
          ...resultInfo,
          is_bc_support_unit_affiliation: checked,
        });
        break;
      case "share_on_hmc":
        setResultInfo({
          ...resultInfo,
          share_on_hmc: checked,
        });
        break;
      default:
        break;
    }
    setChangesHandler(field);
  };

  const fileHandler = (value: File, field: string) => {
    const currentErrors = { ...errors };

    if (value.type !== "application/pdf" && value.type !== "pdf") {
      errors[field] = "Invalid file type, please select a pdf file";
      setErrors(currentErrors);
      return;
    }

    if (value && value.size >= 15 * 1024 * 1024) {
      errors[field] = "Invalid file size, maximum file size 15MB.";
      setErrors(currentErrors);
      return;
    }

    if (value && field === "resource_file") {
      setResourceFile(value);
      setResultInfo({ ...resultInfo, resource_file_name: value.name });
      delete errors.reb_file;

      setErrors(currentErrors);
    }
  };

  const removeFileHandler = () => {
    const currentErrors = { ...errors };
    setResourceFile(undefined);
    setResultInfo({ ...resultInfo, resource_file_name: undefined });
    setErrors(currentErrors);
  };

  const checkForReseacherAccount = async (
    email: string,
    type: string,
    id: string
  ) => {
    setIsSubmiting(true);
    try {
      await emailValidation.validate({ email });

      const researcher = await getResearcherByEmail(email.toLowerCase());
      const updatedErrors = { ...errors };

      if (Object.keys(researcher).length === 0) {
        switch (type) {
          case "pi_email":
            setErrors({ ...errors, pi_email: VALIDATION_CONST.PI_ID.VALID });
            setResultInfo({ ...resultInfo, pi_id: undefined });
            break;
          case "teamMembers":
            updatedErrors[id] = VALIDATION_CONST.TEAM_MEMBERS.VALID;
            setErrors(updatedErrors);
            break;
          default:
            break;
        }
        setIsSubmiting(false);
      } else {
        if (type === "pi_email") {
          setResultInfo({
            ...resultInfo,
            pi_id: researcher.id,
          });
          delete updatedErrors.pi_email;
        }

        if (type === "teamMembers" && id) {
          const updatedTeamMembers = teamMembers;
          updatedTeamMembers[id] = {
            email: researcher.email,
            id: researcher.id,
          };
          setTeamMembers(updatedTeamMembers);

          delete updatedErrors[id];
        }
        setErrors(updatedErrors);
        setIsSubmiting(false);
      }
    } catch (error: any) {
      const errorMessages: { [key: string]: string } = { ...errors };

      switch (type) {
        case "pi_email":
          errorMessages.pi_email = error.message;
          setResultInfo({ ...resultInfo, pi_id: undefined });
          break;
        case "teamMembers":
          errorMessages[id] = error.message;
          break;
        default:
          break;
      }
      setChangesHandler(type);
      setErrors(errorMessages);
      setIsSubmiting(false);
    }
  };

  const submitHandler = async () => {
    setIsSubmiting(true);
    try {
      const standAloneResults: Record<string, any> = {
        ...resultInfo,
        teamMembers: Object.values(teamMembers),
        healthCategories: selectedHealthCategories,
        healthAuthorityRegions: selectedHealthAuthorityRegions,
        regional_centers: selectedRegionalCenters,
        updated_at: new Date(),
      };

      if (Object.keys(collaboratingOrganizations).length > 0)
        standAloneResults.collaborating_organizations = Object.keys(
          collaboratingOrganizations
        )
          .map((id) => collaboratingOrganizations[id])
          .filter((org) => org !== "")
          .join(",");

      const validedResutls = await updateStandeAloneResultValidations.validate(
        standAloneResults,
        { abortEarly: false, stripUnknown: true }
      );

      const data = new FormData();

      Object.keys(validedResutls).forEach((key) => {
        const value = validedResutls[key];

        if (key === "updated_at") {
          data.append(key, new Date(value).toISOString());
        } else if (typeof value === "object" && value !== null) {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      });

      if (resourceFile) {
        data.append("resourceFile", resourceFile);
      }

      data.append("changes", changes.join(","));

      const results = await updateStandAloneResults(data);

      if (results.id) {
        navigate("/success/result", {
          state: {
            id: results.id,
            type: "standAloneResult",
            resultType: "standAloneResult",
          },
        });
        setIsSubmiting(false);
        setChanges([]);
      }
    } catch (error: any) {
      const validationErrors: { [key: string]: string } = {};
      setIsSubmiting(false);

      if (error.inner) {
        error.inner.forEach((element: any) => {
          validationErrors[element.path] = element.message;
        });
        setErrors(validationErrors);
      }

      if (error.message) {
        setErrors(JSON.parse(error.message));
      }
    }
  };

  const getResults = async () => {
    const { id } = params;

    if (!id || id === undefined) throw new Error("Missing info");

    const { result } = await getResultStandAlone(id, undefined, true);
    console.log("🚀 ~ getResults ~ hook:", result);

    if (result.collaborating_organizations) {
      const collaboratorInfo: Record<string, string> = {};
      result.collaborating_organizations.forEach((coll: string) => {
        const collaborId = `collaborating_organizations-${Math.round(
          Math.random() * 10000
        )}`;
        collaboratorInfo[collaborId] = coll;
      });
      setCollaboratingOrganizations(collaboratorInfo);
    }

    if (result.team_members) {
      const teamMemberInfo: Record<string, TeamMemberStateTypes> = {};
      result.team_members.forEach((member: TeamMemberStateTypes) => {
        teamMemberInfo[member.id] = member;
      });
      setTeamMembers(teamMemberInfo);
    }

    setSelectedhealthCategories(result.health_categories);
    setForceChange(!forceChange);

    setSelectedHealthAuthorityRegions(
      result.health_authority_region.map((region: { id: string }) => region.id)
    );

    if (result.regional_centers)
      setSelectedRegionalCenters(result.regional_centers);

    delete result.teamMembers;
    delete result.collaborating_organizations;
    delete result.healthCategories;
    delete result.regional_centers;

    setResultInfo(result);
    console.log("🚀 ~ getResults ~ result:", result);
    setIsLoading(false);
  };

  const getFormValues = async () => {
    const healthCategoriesList = await getHealthCategory();
    setHealthCategories(healthCategoriesList);

    const healthAuthorityRegionList = await getHealthAuthorities();
    setHealthAuthorityRegions(healthAuthorityRegionList);

    const hospitalList = await getHospitals();
    const newArr = hospitalList.map(
      (hospital: { id: number; name: string }) => ({
        value: hospital.id.toString(),
        label: hospital.name,
      })
    );

    setHospitals(newArr);
  };

  useEffect(() => {
    getResults();
    getFormValues();
  }, []);

  return {
    resultInfo,
    handleNavigate,

    updateResultInfoHandler,

    teamMembers,
    collaboratingOrganizations,

    healthAuthorityRegions,
    healthCategories,
    hospitals,
    resourceFile,

    selectedHealthAuthorityRegions,
    selectedHealthCategories,
    selectedRegionalCenters,

    addInputHandler,
    changeAddedInputHandler,
    removeInputHandler,
    setHealthCategoriesHandler,
    forceChange,

    checkboxInputHandler,
    fileHandler,
    removeFileHandler,
    checkForReseacherAccount,

    submitHandler,

    isLoading,
    setIsLoading,
    isSubmiting,
    errors,
  };
};
